/**
 * Subscription Success Screen
 * Phase 2 - Priority 3: Polar.sh Checkout Integration - Phase C
 *
 * Deep link handler for successful subscription purchase.
 * URL: dramadetector://subscription-success
 *
 * Flow:
 * 1. User completes purchase on Polar.sh
 * 2. Polar redirects to deep link
 * 3. This screen shows success message with glassmorphism styling
 * 4. Fetches updated subscription status
 * 5. Auto-redirects to settings after 3 seconds (or user taps CTA)
 *
 * UX Polish: NocoDB #1445
 */
import { useEffect, useState, useRef } from "react";
import { View, Text, ActivityIndicator, Pressable, Animated } from "react-native";
import { useRouter } from "expo-router";
import { useTranslation } from "@dramadetector/shared";
import { useAuth } from "../contexts/AuthContext";
import { useEntitlements } from "../hooks/useEntitlements";
import { trackEvent } from "@/lib/analytics/events";
import { GlassBackground } from "../components/glass/GlassBackground";
import { GlassCard } from "../components/glass/GlassCard";
import { TIER_NAMES } from "../../../lib/polar/constants";

export default function SubscriptionSuccessScreen() {
  const router = useRouter();
  const { t } = useTranslation('subscription');
  const { user } = useAuth();
  const { refetch: refetchEntitlements, tier } = useEntitlements();
  const [countdown, setCountdown] = useState(3);
  const [isSyncing, setIsSyncing] = useState(true);

  // Animated values for checkmark entrance
  const checkScale = useRef(new Animated.Value(0)).current;
  const checkOpacity = useRef(new Animated.Value(0)).current;
  const contentOpacity = useRef(new Animated.Value(0)).current;

  // Resolve display name for the user's tier
  const tierDisplayName = TIER_NAMES[tier] ?? tier;

  useEffect(() => {
    // Animate checkmark in with a spring, then fade in content
    Animated.sequence([
      Animated.parallel([
        Animated.spring(checkScale, {
          toValue: 1,
          tension: 50,
          friction: 6,
          useNativeDriver: true,
        }),
        Animated.timing(checkOpacity, {
          toValue: 1,
          duration: 400,
          useNativeDriver: true,
        }),
      ]),
      Animated.timing(contentOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  useEffect(() => {
    // Track successful subscription
    trackEvent('subscription_purchase_completed', {
      screen: 'subscription_success',
      user_id: user?.id || 'anonymous',
    });

    // Fetch updated subscription status from Supabase
    // The Polar webhook updates the database, and we fetch the latest entitlements here
    const fetchSubscription = async () => {
      try {
        setIsSyncing(true);
        await refetchEntitlements();
        setIsSyncing(false);

        // Track successful sync
        trackEvent('subscription_status_synced', {
          tier,
          user_id: user?.id || 'anonymous',
        });
      } catch (error) {
        console.error('[subscription-success] Failed to fetch subscription status:', error);
        setIsSyncing(false);
        // Non-blocking error - user will see updated status after redirect
      }
    };

    fetchSubscription();

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to settings screen
          router.replace("/(tabs)/settings");
          return 0;
        }
        return prev - 1;
      });
    }, 1000) as unknown as number;

    return () => clearInterval(timer);
  }, [router, user]);

  const handleViewPlan = () => {
    router.replace("/(tabs)/settings");
  };

  return (
    <GlassBackground>
      <View className="flex-1 items-center justify-center p-6">
        <GlassCard>
          <View className="py-4 px-2 items-center">
            {/* Animated Checkmark */}
            <Animated.View
              style={{
                opacity: checkOpacity,
                transform: [{ scale: checkScale }],
              }}
            >
              <View className="w-20 h-20 bg-green-100 rounded-full items-center justify-center mb-5">
                <Text className="text-4xl text-green-600 font-bold">
                  {'\u2713'}
                </Text>
              </View>
            </Animated.View>

            {/* Content fades in after checkmark */}
            <Animated.View style={{ opacity: contentOpacity, width: '100%', alignItems: 'center' }}>
              {/* Title with tier name */}
              <Text className="text-2xl font-bold text-gray-900 mb-2 text-center">
                {t('subscription_success.title_with_tier', {
                  tier: tierDisplayName,
                  defaultValue: `Welcome to ${tierDisplayName}!`,
                })}
              </Text>

              {/* Status line */}
              <Text className="text-sm font-medium text-green-700 mb-2 text-center">
                {t('subscription_success.charge_status', {
                  defaultValue: 'First charge processed — Subscription active',
                })}
              </Text>

              {/* Tier-specific features */}
              {tier === 'drama_plus' && (
                <Text className="text-sm text-gray-500 mb-2 text-center">
                  {t('subscription_success.features_drama_plus', {
                    defaultValue: 'You now have 2,000 char limit and 100 daily analyses',
                  })}
                </Text>
              )}
              {tier === 'dramax' && (
                <Text className="text-sm text-gray-500 mb-2 text-center">
                  {t('subscription_success.features_dramax', {
                    defaultValue: 'You now have 5,000 char limit, 200 daily analyses, and full Narrative Clarity',
                  })}
                </Text>
              )}

              {/* Subtitle */}
              <Text className="text-base text-gray-600 mb-6 text-center">
                {t('subscription_success.message', {
                  defaultValue: 'Your subscription is now active. Enjoy your new features!',
                })}
              </Text>

              {/* Sync status */}
              <View className="flex-row items-center mb-5">
                {isSyncing ? (
                  <>
                    <ActivityIndicator color="#3b82f6" size="small" />
                    <Text className="text-sm text-gray-500 ml-2">
                      {t('subscription_success.syncing', {
                        defaultValue: 'Syncing your subscription...',
                      })}
                    </Text>
                  </>
                ) : (
                  <Text className="text-sm text-green-600 font-medium">
                    {'\u2713'} {t('subscription_success.synced', {
                      defaultValue: 'Subscription synced',
                    })}
                  </Text>
                )}
              </View>

              {/* View Your Plan CTA */}
              <Pressable
                onPress={handleViewPlan}
                className="w-full py-3 rounded-xl bg-teal-600 active:bg-teal-700 items-center mb-4"
                accessibilityRole="button"
                accessibilityLabel="View your plan"
                testID="view-plan-button"
              >
                <Text className="text-base font-semibold text-white">
                  {t('subscription_success.view_plan', {
                    defaultValue: 'View Your Plan',
                  })}
                </Text>
              </Pressable>

              {/* Countdown */}
              <Text className="text-sm text-gray-400 text-center">
                {t('subscription_success.redirecting_countdown', {
                  count: countdown,
                  defaultValue: `Redirecting to your plan in ${countdown} second${countdown !== 1 ? 's' : ''}...`,
                })}
              </Text>
            </Animated.View>
          </View>
        </GlassCard>
      </View>
    </GlassBackground>
  );
}
